import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronRight, LayoutGrid, Sparkles, Trash2 } from 'lucide-react'
import { useId, useMemo, useState } from 'react'

import ScreenLayout from '@/components/layout/ScreenLayout'
import Button from '@/components/ui/Button'
import IconButton from '@/components/ui/IconButton'
import Modal from '@/components/ui/Modal'
import SearchInput from '@/components/ui/SearchInput'
import EmptyState from '@/components/ui/EmptyState'
import QueryState from '@/components/ui/QueryState'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import { SECTION_CARD } from '@/components/ui/section-card-variants'
import { toast } from '@/components/ui/toast-store'
import {
  DashboardUnavailable,
  dashboardsEnabled,
  useBoards,
  useCreateBoard,
  useRemoveBoard,
  type BoardsList,
} from '@/features/dashboard/api/queries'
import { useMitraEnabled } from '@/features/mitra/api/access'
import {
  nameKey,
  type BoardSummary,
  type SaveResult,
  type Template,
} from '@/features/mitra/api/dashboard'
import { useMitraStore } from '@/features/mitra/store'
import { studioTrack } from '@/features/mitra/lib/pagePrefix'
import { cn } from '@/lib/utils'
import NameDialog, { freeName, type NameRefusal } from '@/screens/dashboards/components/NameDialog'
import { MitraDot } from '@/screens/dashboards/components/WidgetCards'
import { StudioGate } from '@/screens/dashboards/StudioGate'

export const Route = createFileRoute('/_app/dashboards')({ component: GatedDashboardsScreen })

/** Only for a reader Mitra is open to (`StudioGate`). */
function GatedDashboardsScreen() {
  return (
    <StudioGate>
      <DashboardsScreen />
    </StudioGate>
  )
}

/**
 * My Dashboards: every dashboard the reader keeps, and three ways to start
 * one -- describe an idea and Mitra builds the whole thing in the chat
 * ("Build me a dashboard: …"), take a template, or start empty (mitra-ai
 * spec §11, 2026-10-18). Each card opens the dashboard at its own URL.
 *
 * Mitra is optional in a build (`config.mitraPrefix`): with no engine there
 * are no dashboards to read, and the page says so rather than failing.
 */

const HOME_SKELETON = (
  <div className="grid grid-cols-1 gap-4 @md:grid-cols-2 @3xl:grid-cols-3">
    {Array.from({ length: 3 }, (_, index) => (
      <Skeleton key={index} className="h-28 rounded-lg" />
    ))}
  </div>
)

/** How many dashboards before the list offers a search. */
const SEARCH_FROM = 7

function BoardCard({ board, onDelete }: { board: BoardSummary; onDelete: () => void }) {
  return (
    // THE WHOLE CARD OPENS THE DASHBOARD, and delete sits in its own column:
    // the title's link stretches over the card (`after:`), the delete button
    // is a sibling raised above it, so a tap on it never opens the dashboard
    // and a button never nests inside a link. Delete is the card's one other
    // action, so it shows as itself rather than behind a ⋯ menu (owner,
    // 2026-10-30); it still asks first, naming the dashboard. The chevron is
    // centred on the card's right edge, clear of the button and the title.
    <li
      data-role="board-card"
      className={cn(
        SECTION_CARD,
        'relative flex min-w-0 items-start gap-3 dark:border-border hover:bg-muted has-[a:focus-visible]:outline-2 has-[a:focus-visible]:outline-offset-2 has-[a:focus-visible]:outline-ring',
      )}
    >
      <LayoutGrid aria-hidden className="mt-1 size-6 shrink-0 text-primary" />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-start gap-2">
          <Link
            to="/dashboards/$dashboardId"
            params={{ dashboardId: board.id }}
            data-role="board-link"
            className="min-w-0 flex-1 text-label-l break-words text-foreground outline-none after:absolute after:inset-0 after:rounded-[inherit]"
          >
            {board.name}
          </Link>
          <MitraDot origin={board.origin} />
        </span>
        {/* No purpose line and no footer (owner, 2026-10-23): a saved board
            kept its template's sentence, and the measure count and date
            told the reader nothing they act on. The name is the card. */}
      </span>
      {/* Centred on the title's 24px line: a 40px button pulled up 8px, its hit
          area grown 4px each side to 48 so it is never smaller than 44. */}
      <span className="relative z-10 -my-2 shrink-0">
        <IconButton
          aria-label={`Delete ${board.name}`}
          title="Delete"
          data-role="board-delete"
          onClick={onDelete}
          className="relative size-10 text-muted-foreground before:absolute before:-inset-1 hover:text-destructive focus-visible:text-destructive"
        >
          <Trash2 className="size-5" aria-hidden="true" />
        </IconButton>
      </span>
      <ChevronRight aria-hidden className="size-5 shrink-0 self-center text-muted-foreground" />
    </li>
  )
}

/**
 * Every dashboard, as many as the reader keeps (owner, 2026-10-20: no
 * limit): two columns on a wide screen, and past a handful a search by name
 * or purpose, so twenty read as easily as three.
 */
function BoardList({
  boards,
  onDelete,
}: {
  boards: readonly BoardSummary[]
  onDelete: (board: BoardSummary) => void
}) {
  const [query, setQuery] = useState('')
  const shown = useMemo(() => {
    const q = nameKey(query)
    return q ? boards.filter((b) => nameKey(`${b.name} ${b.purpose}`).includes(q)) : [...boards]
  }, [boards, query])
  return (
    <section aria-label="Your dashboards" className="flex flex-col gap-3">
      {boards.length >= SEARCH_FROM ? (
        <SearchInput
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
          }}
          aria-label="Search your dashboards"
          placeholder="Search dashboards"
        />
      ) : null}
      <ul data-role="board-list" className="grid grid-cols-1 gap-3 @3xl:grid-cols-2">
        {shown.map((board) => (
          <BoardCard
            key={board.id}
            board={board}
            onDelete={() => {
              onDelete(board)
            }}
          />
        ))}
      </ul>
      {query && shown.length === 0 ? (
        <p className="text-body-s text-muted-foreground">No dashboard matches “{query}”.</p>
      ) : null}
    </section>
  )
}

/** Describe it, and Mitra builds the whole dashboard in the chat. */
function IdeaBox({ onAsk, hero }: { onAsk: (question: string) => void; hero: boolean }) {
  const [idea, setIdea] = useState('')
  const id = useId()
  return (
    <form
      data-role="idea-box"
      onSubmit={(event) => {
        event.preventDefault()
        if (idea.trim()) onAsk(studioTrack(idea.trim()))
      }}
      className={cn(SECTION_CARD, 'flex flex-col gap-2', hero && 'bg-tone-info-soft')}
    >
      <label
        htmlFor={id}
        className={hero ? 'text-h-xs text-foreground' : 'text-label-m text-foreground'}
      >
        New from an idea
      </label>
      <div className="flex flex-col gap-2 @md:flex-row">
        <input
          id={id}
          value={idea}
          onChange={(e) => {
            setIdea(e.target.value)
          }}
          placeholder="For example: the risk in my bank shares"
          className="min-h-11 min-w-0 flex-1 rounded-md border border-border bg-background px-3 text-body-m text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        />
        <Button
          type="submit"
          variant="primary"
          disabled={!idea.trim()}
          className="disabled:bg-muted disabled:text-foreground/70 disabled:opacity-100"
        >
          <Sparkles className="size-4" aria-hidden="true" />
          Build it with Mitra
        </Button>
      </div>
    </form>
  )
}

/**
 * The dashboard a template already made, where the list says (its recorded
 * `template`, or its name for one made before templates were recorded). A
 * template about one company is matched by the engine once the company is
 * named.
 */
function madeFrom(boards: readonly BoardSummary[], template: Template): BoardSummary | null {
  if (template.needsSymbol) return null
  return (
    boards.find((b) => b.template === template.key) ??
    boards.find((b) => b.template === null && nameKey(b.name) === nameKey(template.name)) ??
    null
  )
}

function Templates({
  templates,
  boards,
  disabled,
  onPick,
  onCopy,
}: {
  templates: readonly Template[]
  boards: readonly BoardSummary[]
  disabled: boolean
  onPick: (template: Template, symbol?: string) => void
  /** A second dashboard from a template the reader has: under a new name. */
  onCopy: (template: Template) => void
}) {
  // A template about one company asks which before it is made (the engine
  // refuses `stock_watch` without a symbol).
  const [asking, setAsking] = useState<Template | null>(null)
  const [symbol, setSymbol] = useState('')
  const symbolId = useId()
  const navigate = useNavigate()
  if (templates.length === 0) return null
  const company = symbol.trim().toUpperCase()
  return (
    <section aria-label="Start from a template" className="flex min-w-0 flex-col gap-2">
      <h2 className="text-label-m text-foreground">Start from a template</h2>
      <ul data-role="templates" className="grid grid-cols-1 gap-3 @md:grid-cols-2 @3xl:grid-cols-3">
        {templates.map((template) => {
          const had = madeFrom(boards, template)
          return (
            // One even row per template (owner, 2026-10-23): the name, and a copy
            // action inside the card where the reader already has one.
            <li
              key={template.key}
              className="flex min-w-0 items-center rounded-md border border-border bg-card has-[button:hover]:bg-muted"
            >
              <button
                type="button"
                disabled={disabled}
                aria-label={had ? `Open ${had.name}` : `Start from ${template.name}`}
                // What it holds, measure by measure, on hover.
                title={template.sections.flatMap((s) => s.measures).join(' · ')}
                data-had={had ? 'yes' : undefined}
                onClick={() => {
                  if (had) {
                    // A template the reader has is opened, never made again.
                    void navigate({
                      to: '/dashboards/$dashboardId',
                      params: { dashboardId: had.id },
                    })
                  } else if (template.needsSymbol) {
                    setAsking(template)
                  } else {
                    onPick(template)
                  }
                }}
                className="flex min-w-0 flex-1 items-center rounded-md px-4 py-3 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50"
              >
                {/* The name alone (owner, 2026-10-23): no purpose sentence, no
                    section chips, no "You have this" line. */}
                <span className="truncate text-label-l text-foreground">{template.name}</span>
              </button>
              {had ? (
                <Button
                  size="sm"
                  variant="quiet"
                  disabled={disabled}
                  className="mr-2 shrink-0"
                  onClick={() => {
                    onCopy(template)
                  }}
                >
                  Make a copy
                </Button>
              ) : null}
            </li>
          )
        })}
      </ul>
      {asking ? (
        <form
          data-role="template-symbol"
          onSubmit={(event) => {
            event.preventDefault()
            if (company) onPick(asking, company)
          }}
          className="flex flex-wrap items-end gap-2 rounded-md border border-border-subtle bg-card p-3"
        >
          <label
            htmlFor={symbolId}
            className="flex min-w-40 flex-1 flex-col gap-1 text-label-m text-foreground"
          >
            {`Which company is “${asking.name}” about?`}
            <input
              id={symbolId}
              autoFocus
              value={symbol}
              maxLength={20}
              placeholder="UMHL"
              onChange={(e) => {
                setSymbol(e.target.value)
              }}
              className="min-h-11 rounded-md border border-border bg-background px-3 text-body-m text-foreground uppercase focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            />
          </label>
          <Button type="submit" variant="primary" disabled={disabled || !company}>
            Start
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setAsking(null)
            }}
          >
            Cancel
          </Button>
        </form>
      ) : null}
    </section>
  )
}

/** A name asked for: an empty dashboard, or a copy of a template. */
type Naming = { title: string; initial: string; template?: Template | undefined }

function Home({ list, onAsk }: { list: BoardsList; onAsk: ((question: string) => void) | null }) {
  const create = useCreateBoard()
  const remove = useRemoveBoard()
  const navigate = useNavigate()
  const [naming, setNaming] = useState<Naming | null>(null)
  const [refusal, setRefusal] = useState<NameRefusal | null>(null)
  const [deleting, setDeleting] = useState<BoardSummary | null>(null)
  const open = (id: string) => {
    void navigate({ to: '/dashboards/$dashboardId', params: { dashboardId: id } })
  }
  /** Make one, and say what came of it: opened, refused by name, or failed. */
  const start = (input: { template?: string; name?: string; symbol?: string }, named: boolean) => {
    setRefusal(null)
    void create.mutateAsync({ etag: list.etag, ...input }).then((result: SaveResult) => {
      const id = result.kind === 'saved' ? result.body.dashboard?.id : undefined
      if (result.kind === 'saved' && id) {
        if (result.body.existing)
          toast.info(`You already have “${result.body.dashboard?.name ?? ''}”.`)
        setNaming(null)
        open(id)
      } else if (result.kind === 'refused' && result.code === 'name_taken' && result.existing) {
        // A template the reader has: open it. A name they typed: say so where they typed it.
        if (named) setRefusal({ message: result.message, existing: result.existing })
        else {
          toast.info(result.message)
          open(result.existing)
        }
      } else if (result.kind === 'refused') {
        if (named) setRefusal({ message: result.message, existing: null })
        else toast.error(result.message)
      } else if (result.kind === 'conflict') {
        toast.error('Your dashboards changed in another tab. Reload to see the latest.')
      } else {
        toast.error('The dashboard could not be made just now.')
      }
    })
  }
  const empty = list.boards.length === 0
  return (
    <div data-role="dashboards-home" className="flex flex-col gap-6">
      {/* With none yet, the idea box leads, tinted; the three ways to start are
          the page, so no paragraph above them lists them again. */}
      {onAsk ? <IdeaBox onAsk={onAsk} hero={empty} /> : null}
      {empty ? null : <BoardList boards={list.boards} onDelete={setDeleting} />}
      <Templates
        templates={list.templates}
        boards={list.boards}
        disabled={create.isPending}
        onPick={(template, symbol) => {
          start({ template: template.key, ...(symbol ? { symbol } : {}) }, false)
        }}
        onCopy={(template) => {
          setRefusal(null)
          setNaming({
            title: `A copy of “${template.name}”`,
            initial: freeName(list.boards, `${template.name} copy`),
            template,
          })
        }}
      />
      <div>
        <Button
          variant="secondary"
          disabled={create.isPending}
          onClick={() => {
            setRefusal(null)
            setNaming({ title: 'New dashboard', initial: freeName(list.boards, 'New dashboard') })
          }}
        >
          Start empty
        </Button>
      </div>
      {naming ? (
        <NameDialog
          open
          title={naming.title}
          initial={naming.initial}
          boards={list.boards}
          busy={create.isPending}
          refusal={refusal}
          onClose={() => {
            setNaming(null)
            setRefusal(null)
          }}
          onSave={(name) => {
            start({ name, ...(naming.template ? { template: naming.template.key } : {}) }, true)
          }}
        />
      ) : null}
      <Modal
        open={deleting !== null}
        onOpenChange={(next) => {
          if (!next) setDeleting(null)
        }}
        title={`Delete “${deleting?.name ?? ''}”?`}
        description="Its measures go with it. Your holdings and watchlists are not touched."
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setDeleting(null)
              }}
            >
              Keep it
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => {
                const gone = deleting
                if (!gone) return
                void remove.mutateAsync({ id: gone.id, etag: list.etag }).then((result) => {
                  setDeleting(null)
                  if (result.kind === 'removed') toast.info(`Deleted “${gone.name}”.`)
                  else if (result.kind === 'conflict')
                    toast.error('Your dashboards changed in another tab. Reload to see the latest.')
                  else toast.error('That dashboard could not be deleted just now.')
                })
              }}
            >
              Delete
            </Button>
          </div>
        }
      />
    </div>
  )
}

function DashboardsScreen() {
  const query = useBoards()
  const openPanel = useMitraStore((state) => state.openPanel)
  const askMitra = useMitraEnabled()
    ? (question: string) => {
        openPanel(question)
      }
    : null
  const unavailable =
    !dashboardsEnabled() ||
    (query.error instanceof DashboardUnavailable && query.error.reason === 'unavailable')

  return (
    <ScreenLayout
      header={
        <TitleRow
          title="Analytics Studio"
          subtitle="Your own measures on your holdings, watchlists or the market."
        />
      }
      pageTitle="Analytics Studio"
    >
      {unavailable ? (
        <EmptyState
          size="page"
          title="Dashboards are not available here"
          description="Your dashboards are kept by Mitra, which is not configured for this app yet."
        />
      ) : (
        <QueryState
          query={query}
          skeleton={HOME_SKELETON}
          loadingLabel="Opening your dashboards…"
          size="page"
          failed={{ title: 'Your dashboards did not load' }}
        >
          {(list) => <Home list={list} onAsk={askMitra} />}
        </QueryState>
      )}
    </ScreenLayout>
  )
}
